import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Typography } from
'@mui/material';
import { BlockingStrategyStats, DeduplicationTask } from 'crqlar-react-common/dist/models/profile/deduplicationTask.model';
import { formatBytes, formatDuration, formatNumber } from './deduplicationUtils';

interface BlockingPhaseCardProps {
  task: DeduplicationTask;
}

const BlockingPhaseCard: React.FC<BlockingPhaseCardProps> = ({ task }) => {
    const { t } = useTranslation();
    if (!task.blockingStats) return null;
    const stats = task.blockingStats;

  const renderProgressBar = (label: string, value: number, max: number, color: 'primary' | 'secondary' | 'success' | 'warning' = 'primary') => {
    const percentage = max > 0 ? value / max * 100 : 0;
    return (
      <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>{label}</Typography>
                    <Typography variant="h6" color="text.secondary">
                        {formatNumber(value)} / {formatNumber(max)} ({percentage.toFixed(4)}%)
                    </Typography>
                </Box>
                <LinearProgress
          variant="determinate"
          value={Math.min(percentage, 100)}
          color={color}
          sx={{ height: 12, borderRadius: 2 }} />

            </Box>);

  };

  return (
    <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
            <CardContent>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>{t("Step 1: Blocking / Indexing")}

        </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>{t("Duration:")}
          {formatDuration(stats.durationMs)}
                </Typography>

                <Grid container spacing={4} mb={6}>
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ bgcolor: 'background.default' }}>
                            <Typography variant="h6" color="primary" gutterBottom>{t("Profile Processing")}

              </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 700 }}>
                                {formatNumber(stats.totalProfilesProcessed)}
                            </Typography>
                            <Typography variant="h6" color="text.secondary">{t("Profiles indexed")}

              </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ bgcolor: 'background.default' }}>
                            <Typography variant="h6" color="primary" gutterBottom>{t("Index Size")}

              </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 700 }}>
                                {formatBytes(stats.estimatedIndexSizeBytes)}
                            </Typography>
                            <Typography variant="h6" color="text.secondary">{t("Memory footprint")}

              </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>{t("Complexity Reduction")}

          </Typography>
                    {stats.totalPossiblePairs && stats.candidatePairsGenerated &&
          <>
                            {renderProgressBar(
              'Candidate pairs generated (unique)',
              stats.candidatePairsGenerated,
              stats.totalPossiblePairs,
              'success'
            )}
                            <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>{t("\u2713 Eliminated")}
              {formatNumber(stats.totalPossiblePairs - stats.candidatePairsGenerated)} comparisons
                                ({stats.reductionPercentage?.toFixed(5)}{t("% reduction)")}
            </Typography>
                        </>
          }
                </Box>

                <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>{t("Blocking Strategies")}

          </Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={6} md={3}>
                            <Chip label={`${stats.strategyCount || 0} strategies`} size="small" />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Chip label={`${formatNumber(stats.indexKeyCount)} keys`} size="small" />
                        </Grid>
                    </Grid>
                </Box>

                {stats.strategyDistribution && stats.strategyDistribution.length > 0 &&
        <Box sx={{ mt: 4 }}>
                        <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>{t("Strategy Distribution")}

          </Typography>

                        {/* Key Count Histogram */}
                        {/*<Box sx={{mb: 4}}>*/}
                        {/*    <Typography variant="h6" gutterBottom sx={{fontWeight: 500}}>*/}
                        {/*        Keys per Strategy*/}
                        {/*    </Typography>*/}
                        {/*    {stats.strategyDistribution.map((strategyStats, index) => {*/}
                        {/*        const maxKeys = Math.max(...stats.strategyDistribution!.map(s => s.keyCount));*/}
                        {/*        const percentage = maxKeys > 0 ? (strategyStats.keyCount / maxKeys) * 100 : 0;*/}
                        {/*        return (*/}
                        {/*            <Box key={index} sx={{mb: 2}}>*/}
                        {/*                <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 0.5}}>*/}
                        {/*                    <Typography variant="body2" sx={{fontWeight: 500}}>*/}
                        {/*                        {strategyStats.strategy}*/}
                        {/*                    </Typography>*/}
                        {/*                    <Typography variant="body2" color="text.secondary">*/}
                        {/*                        {formatNumber(strategyStats.keyCount)} keys*/}
                        {/*                    </Typography>*/}
                        {/*                </Box>*/}
                        {/*                <Box*/}
                        {/*                    sx={{*/}
                        {/*                        width: '100%',*/}
                        {/*                        height: 32,*/}
                        {/*                        bgcolor: 'grey.100',*/}
                        {/*                        borderRadius: 1,*/}
                        {/*                        overflow: 'hidden'*/}
                        {/*                    }}*/}
                        {/*                >*/}
                        {/*                    <Box*/}
                        {/*                        sx={{*/}
                        {/*                            width: `${percentage}%`,*/}
                        {/*                            height: '100%',*/}
                        {/*                            bgcolor: 'primary.main',*/}
                        {/*                            transition: 'width 0.3s ease',*/}
                        {/*                            display: 'flex',*/}
                        {/*                            alignItems: 'center',*/}
                        {/*                            px: 1*/}
                        {/*                        }}*/}
                        {/*                    />*/}
                        {/*                </Box>*/}
                        {/*            </Box>*/}
                        {/*        );*/}
                        {/*    })}*/}
                        {/*</Box>*/}

                        {/* Candidate Pairs Histogram */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>{t("Candidate Pairs per Strategy")}

            </Typography>
                            {stats.strategyDistribution.map((strategyStats, index) => {
              const maxPairs = Math.max(...stats.strategyDistribution!.map((s) => s.candidatePairs));
              const percentage = maxPairs > 0 ? strategyStats.candidatePairs / maxPairs * 100 : 0;
              return (
                <Box key={index} sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {strategyStats.strategy}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {formatNumber(strategyStats.candidatePairs)} {t("pairs")}
                    </Typography>
                                        </Box>
                                        <Box
                    sx={{
                      width: '100%',
                      height: 32,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      overflow: 'hidden'
                    }}>

                                            <Box
                      sx={{
                        width: `${percentage}%`,
                        height: '100%',
                        bgcolor: 'success.main',
                        transition: 'width 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        px: 1
                      }} />

                                        </Box>
                                    </Box>);

            })}
                        </Box>

                        {/* Average Block Size Histogram */}
                        {/*<Box sx={{mb: 2}}>*/}
                        {/*    <Typography variant="h6" gutterBottom sx={{fontWeight: 500}}>*/}
                        {/*        Average Block Size per Strategy*/}
                        {/*    </Typography>*/}
                        {/*    {stats.strategyDistribution.map((strategyStats, index) => {*/}
                        {/*        const maxAvgSize = Math.max(...stats.strategyDistribution!.map(s => s.avgBlockSize));*/}
                        {/*        const percentage = maxAvgSize > 0 ? (strategyStats.avgBlockSize / maxAvgSize) * 100 : 0;*/}
                        {/*        return (*/}
                        {/*            <Box key={index} sx={{mb: 2}}>*/}
                        {/*                <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 0.5}}>*/}
                        {/*                    <Typography variant="body2" sx={{fontWeight: 500}}>*/}
                        {/*                        {strategyStats.strategy}*/}
                        {/*                    </Typography>*/}
                        {/*                    <Typography variant="body2" color="text.secondary">*/}
                        {/*                        {strategyStats.avgBlockSize.toFixed(2)} avg*/}
                        {/*                    </Typography>*/}
                        {/*                </Box>*/}
                        {/*                <Box*/}
                        {/*                    sx={{*/}
                        {/*                        width: '100%',*/}
                        {/*                        height: 32,*/}
                        {/*                        bgcolor: 'grey.100',*/}
                        {/*                        borderRadius: 1,*/}
                        {/*                        overflow: 'hidden'*/}
                        {/*                    }}*/}
                        {/*                >*/}
                        {/*                    <Box*/}
                        {/*                        sx={{*/}
                        {/*                            width: `${percentage}%`,*/}
                        {/*                            height: '100%',*/}
                        {/*                            bgcolor: 'warning.main',*/}
                        {/*                            transition: 'width 0.3s ease',*/}
                        {/*                            display: 'flex',*/}
                        {/*                            alignItems: 'center',*/}
                        {/*                            px: 1*/}
                        {/*                        }}*/}
                        {/*                    />*/}
                        {/*                </Box>*/}
                        {/*            </Box>*/}
                        {/*        );*/}
                        {/*    })}*/}
                        {/*</Box>*/}
                    </Box>
        }
            </CardContent>
        </Card>);

};

export default BlockingPhaseCard;
